import React, { useState, useCallback, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { Image, Upload, X, Loader2, Clipboard, Bug } from "lucide-react";
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import { logger } from "@/lib/logger";

interface UploadedImage {
  id: string;
  file: File;
  preview: string;
  uploading: boolean;
  url?: string;
  error?: string;
}

const IssueSection = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("bug");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [isPasting, setIsPasting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();
  const { t } = useLanguage();
  
  // Handle file drops and selections
  const onDrop = useCallback((acceptedFiles: File[]) => {
    handleFiles(acceptedFiles);
  }, [uploadedImages.length, t, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': []
    },
    maxSize: 5 * 1024 * 1024, // 5MB
    maxFiles: 5
  });

  // Common function to process files (used by both drop and paste)
  const handleFiles = (files: File[]) => {
    // Filter for only image files and limit to 5MB
    const imageFiles = files.filter(file => 
      file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024
    );
    
    if (imageFiles.length < files.length) {
      toast({
        variant: "destructive",
        title: t('settings.issues.imageUpload.invalidFiles'),
        description: t('settings.issues.imageUpload.sizeTypeLimit'),
      });
    }

    // Limit total number of images to 5
    const remainingSlots = 5 - uploadedImages.length;
    if (remainingSlots <= 0) {
      toast({
        variant: "destructive",
        title: t('settings.issues.imageUpload.tooManyImages'),
        description: t('settings.issues.imageUpload.limitReached'),
      });
      return;
    }

    // Only take as many files as we have slots for
    const filesToAdd = imageFiles.slice(0, remainingSlots);

    // Add new images to state
    const newImages = filesToAdd.map(file => ({
      id: uuidv4(),
      file,
      preview: URL.createObjectURL(file),
      uploading: false
    }));

    setUploadedImages(prev => [...prev, ...newImages]);
  };
  
  // Handle paste events in the textarea
  const handlePaste = useCallback(async (e: ClipboardEvent) => {
    if (!e.clipboardData) return;
    
    // Check if we're pasting into the textarea
    const activeElement = document.activeElement;
    if (activeElement !== textareaRef.current) return;
    
    setIsPasting(true);
    
    try {
      const items = e.clipboardData.items;
      const imageItems = Array.from(items).filter(item => item.type.startsWith('image/'));
      
      if (imageItems.length === 0) {
        // No images found in clipboard, let the normal paste behavior occur
        setIsPasting(false);
        return;
      }
      
      e.preventDefault(); // Prevent the default paste behavior
      
      const files = await Promise.all(
        imageItems.map(async (item) => {
          const blob = item.getAsFile();
          if (!blob) return null;
          
          // Create a file from the blob with a proper name
          return new File(
            [blob], 
            `clipboard-image-${Date.now()}.${item.type.split('/')[1] || 'png'}`, 
            { type: item.type }
          );
        })
      );
      
      // Filter out null entries
      const validFiles = files.filter(file => file !== null) as File[];
      
      // Process the files
      handleFiles(validFiles);
      
      // Show a success toast
      if (validFiles.length > 0) {
        toast({
          title: t('settings.issues.imageUpload.pasteSuccess'),
          description: t('settings.issues.imageUpload.pasteSuccessDesc'),
        });
      }
    } catch (error) {
      logger.error("Error handling clipboard paste:", error);
      toast({
        variant: "destructive",
        title: t('settings.issues.imageUpload.pasteFailed'),
        description: t('settings.issues.imageUpload.pasteFailedDesc'),
      });
    } finally {
      setIsPasting(false);
    }
  }, [handleFiles, uploadedImages.length, textareaRef, toast, t]);
  
  // Add and remove the paste event listener
  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste]);

  const removeImage = (id: string) => {
    setUploadedImages(prevImages => {
      const imagesToKeep = prevImages.filter(image => image.id !== id);
      
      // Revoke the object URL to avoid memory leaks
      const imageToRemove = prevImages.find(image => image.id === id);
      if (imageToRemove && imageToRemove.preview) {
        URL.revokeObjectURL(imageToRemove.preview);
      }
      
      return imagesToKeep;
    });
  };

  // Upload an image to Supabase Storage
  const uploadImageToStorage = async (image: UploadedImage): Promise<string | null> => {
    try {
      // Mark image as uploading
      setUploadedImages(prev => prev.map(img => 
        img.id === image.id ? { ...img, uploading: true } : img
      ));

      // Create a unique file path
      const fileExt = image.file.name.split('.').pop();
      const filePath = `${Date.now()}-${Math.floor(Math.random() * 1000)}.${fileExt}`;
      const fullPath = `issue-attachments/${filePath}`;

      // Upload the file
      const { error: uploadError, data } = await supabase.storage
        .from('user-uploads')
        .upload(fullPath, image.file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('user-uploads')
        .getPublicUrl(fullPath);

      // Update state with the URL
      setUploadedImages(prev => prev.map(img => 
        img.id === image.id ? { ...img, url: publicUrl, uploading: false } : img
      ));

      return publicUrl;
    } catch (error) {
      logger.error('Error uploading image:', error);
      
      // Update state with error
      setUploadedImages(prev => prev.map(img => 
        img.id === image.id ? { ...img, error: 'Upload failed', uploading: false } : img
      ));
      
      return null;
    }
  };

  // Upload all images and return their URLs
  const uploadAllImages = async (): Promise<string[]> => {
    const uploadPromises = uploadedImages.map(image => uploadImageToStorage(image));
    const urls = await Promise.all(uploadPromises);
    return urls.filter(url => url !== null) as string[];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Upload any images and get their URLs
      const imageUrls = await uploadAllImages();
      
      // Format description with image links in markdown format
      let formattedDescription = description;
      if (imageUrls.length > 0) {
        formattedDescription += '\n\n## Attached Images\n\n';
        imageUrls.forEach(url => {
          formattedDescription += `![Image](${url})\n\n`;
        });
      }

      // Submit the issue with the formatted description
      const { data, error } = await supabase.functions.invoke("issue-reporter", {
        body: { 
          title, 
          description: formattedDescription, 
          priority,
          imageUrls // Pass the URLs separately in case we need them
        },
      });

      if (error) throw error;

      toast({
        title: t('settings.issues.submitSuccess'),
        description: t('settings.issues.submitSuccessDesc'),
      });
      
      // Reset form
      setTitle("");
      setDescription("");
      setPriority("bug");
      
      // Clear uploaded images and revoke object URLs
      uploadedImages.forEach(image => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
      });
      setUploadedImages([]);
    } catch (error) {
      logger.error("Error reporting issue:", error);
      toast({
        variant: "destructive",
        title: t('settings.issues.submitError'),
        description: t('settings.issues.submitErrorDesc'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clean up created object URLs when component unmounts
  React.useEffect(() => {
    return () => {
      uploadedImages.forEach(image => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, []);

  return (
    <Card className="p-2 sm:p-6 py-4 bg-white/5 backdrop-blur-sm border border-white/10">
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Bug className="h-6 w-6 text-white" />
          <h2 className="text-2xl font-bold text-white">{t('settings.issues.title')}</h2>
        </div>
        <p className="text-sm text-gray-400">{t('settings.issues.description')}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="issue-title" className="text-white">
              {t('settings.issues.form.titleLabel')}
            </Label>
            <Input
              id="issue-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder={t('settings.issues.form.titlePlaceholder')}
              className="w-full"
              maxLength={10000}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="issue-description" className="text-white flex items-center gap-2">
              {t('settings.issues.form.descriptionLabel')}
              <div className="inline-flex items-center ml-2 text-xs text-gray-400 bg-white/5 rounded px-2 py-0.5">
                <Clipboard className="h-3 w-3 mr-1" />
                {t('settings.issues.imageUpload.pasteSupported')}
              </div>
            </Label>
            <Textarea
              id="issue-description"
              ref={textareaRef}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={4}
              placeholder={t('settings.issues.form.descriptionPlaceholder')}
              className="w-full relative"
            />
            {isPasting && (
              <div className="absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center rounded">
                <div className="bg-background p-4 rounded-lg shadow-lg flex items-center gap-3">
                  <Loader2 className="animate-spin h-5 w-5 text-primary" />
                  <span>{t('settings.issues.imageUpload.processing')}</span>
                </div>
              </div>
            )}
          </div>

          {/* Image Upload Area */}
          <div className="space-y-2">
            <Label className="text-white">
              {t('settings.issues.form.imagesLabel')}
            </Label>
            <div 
              {...getRootProps()} 
              className={`border-2 border-dashed rounded-lg p-4 transition-colors cursor-pointer ${
                isDragActive 
                  ? 'border-primary bg-primary/10' 
                  : 'border-gray-500 hover:border-primary/50'
              }`}
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center justify-center text-center p-4">
                <Upload className="h-10 w-10 text-gray-400 mb-2" />
                {isDragActive ? (
                  <p className="text-sm text-gray-300">{t('settings.issues.imageUpload.dropHere')}</p>
                ) : (
                  <>
                    <p className="text-sm text-gray-300">{t('settings.issues.imageUpload.dragDrop')}</p>
                    <p className="text-xs text-gray-400 mt-1">{t('settings.issues.imageUpload.clickToSelect')}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {t('settings.issues.imageUpload.maxSize')} • {t('settings.issues.imageUpload.maxNumber')}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Image Previews */}
          {uploadedImages.length > 0 && (
            <div className="space-y-2">
              <Label className="text-white">
                {t('settings.issues.imageUpload.attachedImages')}
              </Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {uploadedImages.map(image => (
                  <div 
                    key={image.id} 
                    className="relative group rounded-lg overflow-hidden border border-gray-700 bg-black/20"
                  >
                    <div className="aspect-video w-full relative">
                      <img
                        src={image.preview}
                        alt="Preview"
                        className="h-full w-full object-cover"
                      />
                      {image.uploading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                          <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        </div>
                      )}
                      {image.error && (
                        <div className="absolute inset-0 flex items-center justify-center bg-red-900/60">
                          <p className="text-xs text-white px-2 text-center">{image.error}</p>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeImage(image.id)}
                      className="absolute top-1 right-1 bg-black/60 rounded-full p-1 text-gray-300 hover:text-white hover:bg-red-500/90 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <div className="p-2">
                      <p className="text-xs text-gray-400 truncate" title={image.file.name}>
                        {image.file.name.length > 20 
                          ? `${image.file.name.substring(0, 17)}...` 
                          : image.file.name
                        }
                      </p>
                      <p className="text-xs text-gray-500">
                        {(image.file.size / 1024).toFixed(0)} KB
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="issue-priority" className="text-white">
              {t('settings.issues.form.typeLabel')}
            </Label>
            <Select
              onValueChange={setPriority}
              value={priority}
            >
              <SelectTrigger id="issue-priority" className="w-full sm:w-[200px]">
                <SelectValue placeholder={t('settings.issues.form.typePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bug">{t('settings.issues.form.typeBug')}</SelectItem>
                <SelectItem value="enhancement">{t('settings.issues.form.typeRequest')}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-400">
              {t('settings.issues.form.typeDescription')}
            </p>
          </div>

          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('settings.issues.form.submitting')}
              </>
            ) : (
              t('settings.issues.form.submit')
            )}
          </Button>
        </form>
      </div>
    </Card>
  );
};

export default IssueSection;
